/**
 * DevAuthPersonaBar — local-only QA role switcher (Cindy matrix without passwords).
 *
 * Shown on the login page when VITE_DEV_AUTH=true and VITE_DEV_AUTH_PERSONAS=true.
 * Uses dev-token / dev-token:<slug> Bearer tokens honored by the backend in development.
 */

import { useAuthStore } from '@/stores/auth.store';
import {
	DEV_AUTH_PERSONAS,
	devPersonaToSession,
	getStoredDevPersonaSlug,
	isDevAuthPersonasEnabled,
	persistDevPersonaSlug,
} from '@/lib/dev-auth-personas';

export function DevAuthPersonaBar() {
	const enabled = isDevAuthPersonasEnabled();
	const activeSlug = getStoredDevPersonaSlug();
	const { setUser, setTokens } = useAuthStore();

	if (!enabled) return null;

	function handleSelect(slug: string) {
		const persona = DEV_AUTH_PERSONAS.find((p) => p.slug === slug);
		if (!persona) return;
		persistDevPersonaSlug(slug);
		const session = devPersonaToSession(persona);
		setTokens({ accessToken: persona.token, refreshToken: 'dev-refresh' });
		setUser(session);
		window.location.assign('/v2/dashboard');
	}

	return (
		<div
			className="mt-6 rounded-lg border border-dashed border-warning/60 bg-warning-bg p-4"
			data-testid="dev-auth-persona-bar"
		>
			<p className="text-sm font-medium text-warning">
				Local QA bypass — pick a role (dev only)
			</p>
			<p className="mt-1 text-xs text-muted-foreground">
				Mirrors Cindy&apos;s Stage QA accounts. For auth-flow defects, use real password login instead.
			</p>
			<div className="mt-3 flex flex-wrap gap-2">
				{DEV_AUTH_PERSONAS.map((persona) => (
					<button
						key={persona.slug}
						type="button"
						onClick={() => handleSelect(persona.slug)}
						className={`rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
							activeSlug === persona.slug
								? 'border-warning bg-warning text-background'
								: 'border-border bg-background hover:bg-muted'
						}`}
					>
						{persona.label}
					</button>
				))}
			</div>
		</div>
	);
}
